import { ExclamationIcon, PencilIcon } from '@heroicons/react/outline';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import Button from '../../../components/Button';
import Dialog from '../../../components/Dialog';
import { notifyError } from '../../../utils/notify';
import ProjectWizardCancelDialog from './ProjectWizardCancelDialog';
import ProjectWizardClearDialog from './ProjectWizardClearDialog';
import ProjectWizardRequestEditDialog from './ProjectWizardRequestEditDialog';
import ProjectWizardTakeoverRequestDialog from './ProjectWizardTakeoverRequestDialog';

const ProjectWizardEditControls = ({
  isEditMode,
  canEditProject,
  isProjectReady,
  isEditLocked,
  hasDraft,
  onEnterEditMode,
  onExitEditMode,
  onSaveDraft,
  onPreparePublish,
  onClearDraft,
  onCommitDraft,
  onCheckPendingChanges,
  onDiscardChanges,
  hasPendingChangesRef,
  onRegisterLeaveConfirmation,
  projectId,
  requestEditDialog,
  takeoverRequestDialog,
}) => {
  const history = useHistory();
  const pendingNavigationRef = useRef(null);
  const historyUnblockRef = useRef(null);
  const isWithinCurrentProjectWizard = useCallback(
    (pathname) => pathname.startsWith(`/settings/projects/${projectId}/`),
    [projectId],
  );
  const [isPublishDialogVisible, setIsPublishDialogVisible] = useState(false);
  const [isPreparingPublish, setIsPreparingPublish] = useState(false);
  const [canCommitDraft, setCanCommitDraft] = useState(false);
  const [isCancelDialogVisible, setIsCancelDialogVisible] = useState(false);
  const [isClearDialogVisible, setIsClearDialogVisible] = useState(false);
  const [isSavingDraft, setIsSavingDraft] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);
  const [isClearingDraft, setIsClearingDraft] = useState(false);

  const handleOpenPublishDialog = () => {
    if (hasPendingChangesRef?.current) {
      notifyError({ useWarning: true })(
        'There are unsaved changes. Please save your changes before publishing.',
      );
      return;
    }

    setIsPreparingPublish(true);
    onPreparePublish()
      .then(({ hasPendingChanges }) => {
        setCanCommitDraft(hasPendingChanges);
        setIsPublishDialogVisible(true);
      })
      .finally(() => setIsPreparingPublish(false));
  };

  const handleClosePublishDialog = () => {
    setIsPublishDialogVisible(false);
    setCanCommitDraft(false);
  };

  const handleCancelEdit = () => {
    requestLeaveConfirmation(null);
  };

  const handleCloseCancelDialog = () => {
    setIsCancelDialogVisible(false);
    pendingNavigationRef.current = null;
  };

  const completeLeaveAfterCancelAction = (navigate) => {
    onExitEditMode();
    if (navigate) {
      if (historyUnblockRef.current) {
        historyUnblockRef.current();
        historyUnblockRef.current = null;
      }
      navigate();
    }
  };

  const requestLeaveConfirmation = useCallback(
    (navigate) => {
      onCheckPendingChanges().then(({ hasPendingChanges }) => {
        if (hasPendingChanges) {
          pendingNavigationRef.current = navigate;
          setIsCancelDialogVisible(true);
          return;
        }
        if (navigate) {
          navigate();
          return;
        }
        onExitEditMode();
      });
    },
    [onCheckPendingChanges, onExitEditMode],
  );

  useEffect(() => {
    if (!onRegisterLeaveConfirmation) {
      return undefined;
    }
    onRegisterLeaveConfirmation(requestLeaveConfirmation);
    return () => onRegisterLeaveConfirmation(null);
  }, [onRegisterLeaveConfirmation, requestLeaveConfirmation]);

  useEffect(() => {
    if (!isEditMode) {
      return undefined;
    }

    const handleBeforeUnload = (event) => {
      if (!hasPendingChangesRef?.current) {
        return;
      }
      event.preventDefault();
      event.returnValue = '';
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasPendingChangesRef, isEditMode]);

  useEffect(() => {
    if (!isEditMode || !hasPendingChangesRef) {
      return undefined;
    }

    historyUnblockRef.current = history.block((location) => {
      if (!hasPendingChangesRef.current) {
        return undefined;
      }

      if (isWithinCurrentProjectWizard(location.pathname)) {
        return undefined;
      }

      pendingNavigationRef.current = () =>
        history.push(`${location.pathname}${location.search}${location.hash}`);
      setIsCancelDialogVisible(true);
      return false;
    });

    return () => {
      if (historyUnblockRef.current) {
        historyUnblockRef.current();
        historyUnblockRef.current = null;
      }
    };
  }, [hasPendingChangesRef, history, isEditMode, isWithinCurrentProjectWizard]);

  const handleDiscardAndExit = () => {
    const navigate = pendingNavigationRef.current;
    pendingNavigationRef.current = null;
    setIsCancelDialogVisible(false);
    onDiscardChanges().then(() => completeLeaveAfterCancelAction(navigate));
  };

  const handleSaveDraftFromCancel = () => {
    setIsSavingDraft(true);
    onSaveDraft()
      .then(() => {
        const navigate = pendingNavigationRef.current;
        pendingNavigationRef.current = null;
        setIsCancelDialogVisible(false);
        completeLeaveAfterCancelAction(navigate);
      })
      .finally(() => setIsSavingDraft(false));
  };

  const handlePublishFromCancel = () => {
    setIsPublishing(true);
    onCommitDraft()
      .then(() => {
        const navigate = pendingNavigationRef.current;
        pendingNavigationRef.current = null;
        setIsCancelDialogVisible(false);
        completeLeaveAfterCancelAction(navigate);
      })
      .finally(() => setIsPublishing(false));
  };

  const handleOpenClearDialog = () => {
    setIsClearDialogVisible(true);
  };

  const handleCloseClearDialog = () => {
    setIsClearDialogVisible(false);
  };

  const handleConfirmClear = () => {
    setIsClearingDraft(true);
    onClearDraft()
      .then(() => handleCloseClearDialog())
      .finally(() => setIsClearingDraft(false));
  };

  if (!isProjectReady) {
    return null;
  }

  if (!canEditProject) {
    return (
      <span
        data-testid="label-view-only"
        className="inline-flex items-center rounded-full border border-info bg-info-muted dark:bg-neutral-800 px-3 py-1 text-xs font-medium text-on-info dark:text-info whitespace-nowrap"
      >
        View only
      </span>
    );
  }

  if (!isEditMode) {
    return (
      <div className="cluster-3 items-center">
        <span
          data-testid="label-view-only"
          className="inline-flex items-center rounded-full border border-info bg-info-muted dark:bg-neutral-800 px-3 py-1 text-xs font-medium text-on-info dark:text-info whitespace-nowrap"
        >
          View only
        </span>
        <Button
          data-testid="button-EditProject"
          type="primary"
          icon={<PencilIcon className="h-4 w-4" />}
          onClick={onEnterEditMode}
        >
          Edit
        </Button>
        {requestEditDialog && (
          <ProjectWizardRequestEditDialog
            visible={requestEditDialog.visible}
            holderDisplayName={requestEditDialog.holderDisplayName}
            isWaiting={requestEditDialog.isWaiting}
            isRequesting={requestEditDialog.isRequesting}
            onCancel={requestEditDialog.onCancel}
            onRequest={requestEditDialog.onRequest}
          />
        )}
      </div>
    );
  }

  return (
    <>
      {takeoverRequestDialog && (
        <ProjectWizardTakeoverRequestDialog
          visible={takeoverRequestDialog.visible}
          requesterDisplayName={takeoverRequestDialog.requesterDisplayName}
          isResponding={takeoverRequestDialog.isResponding}
          onDeny={takeoverRequestDialog.onDeny}
          onAccept={takeoverRequestDialog.onAccept}
        />
      )}
      <div className="cluster-3 items-center">
        <span
          data-testid="label-editing"
          className="inline-flex items-center rounded-full border border-success bg-success-muted dark:bg-neutral-800 px-3 py-1 text-xs font-medium text-on-success dark:text-success whitespace-nowrap"
        >
          Editing
        </span>
        <Button
          data-testid="button-SaveDraft"
          type="secondary"
          onClick={() => {
            setIsSavingDraft(true);
            onSaveDraft().finally(() => setIsSavingDraft(false));
          }}
          loading={isSavingDraft}
        >
          Save
        </Button>
        <Button
          data-testid="button-PublishChanges"
          type={hasDraft ? 'primary' : 'secondary'}
          loading={isPreparingPublish}
          onClick={handleOpenPublishDialog}
        >
          Publish changes
        </Button>
        <Button
          data-testid="button-draft-clear"
          type="danger"
          disabled={!hasDraft}
          onClick={handleOpenClearDialog}
        >
          Clear
        </Button>
        <Button data-testid="button-CancelEdit" onClick={handleCancelEdit}>
          Close editor
        </Button>
      </div>
      <Dialog
        title="Publish changes?"
        visible={isPublishDialogVisible}
        onCancel={handleClosePublishDialog}
        footer={
          <div className="cluster-2">
            <Button
              data-testid="button-draft-cancel"
              onClick={handleClosePublishDialog}
            >
              Close
            </Button>
            <Button
              data-testid="button-draft-save"
              type="primary"
              disabled={!hasDraft && !canCommitDraft}
              onClick={() => {
                handleClosePublishDialog();
                onCommitDraft();
              }}
            >
              Confirm
            </Button>
          </div>
        }
      >
        {hasDraft || canCommitDraft ? (
          <div className="stack-4">
            <p className="text-sm text-neutral-300 dark:text-neutral-300 m-0">
              This will make the latest project configuration visible to users.
            </p>
            <div className="cluster-3 items-start rounded-md border border-warning bg-warning-muted dark:bg-neutral-800 p-4 text-on-warning dark:text-warning">
              <ExclamationIcon className="h-5 w-5 flex-shrink-0 mt-0.5" />
              <div className="stack-1">
                <p className="font-medium m-0">
                  Publishing updates the live project.
                </p>
                <p className="text-sm m-0">
                  Patients and providers may see these changes after publishing.
                  Review the project settings before confirming.
                </p>
              </div>
            </div>
            <div className="cluster-3 items-start rounded-md border border-warning bg-warning-muted dark:bg-neutral-800 p-4 text-on-warning dark:text-warning">
              <ExclamationIcon className="h-5 w-5 flex-shrink-0 mt-0.5" />
              <div className="stack-1">
                <p className="font-medium m-0">
                  Participations and activities will be scanned after
                  publishing.
                </p>
                <p className="text-sm m-0">
                  After publish, all related recipient participation and
                  activity will be scanned and checked to ensure alignment with
                  the latest project configuration.
                </p>
              </div>
            </div>
          </div>
        ) : (
          <p className="m-0">No pending changes. The project is up to date.</p>
        )}
      </Dialog>
      <ProjectWizardClearDialog
        visible={isClearDialogVisible}
        onCancel={handleCloseClearDialog}
        onConfirm={handleConfirmClear}
        isClearing={isClearingDraft}
      />
      <ProjectWizardCancelDialog
        visible={isCancelDialogVisible}
        onDismiss={handleCloseCancelDialog}
        onDiscard={handleDiscardAndExit}
        onSaveDraft={handleSaveDraftFromCancel}
        onPublish={handlePublishFromCancel}
        isSavingDraft={isSavingDraft}
        isPublishing={isPublishing}
      />
    </>
  );
};

ProjectWizardEditControls.propTypes = {
  isEditMode: PropTypes.bool.isRequired,
  canEditProject: PropTypes.bool.isRequired,
  isProjectReady: PropTypes.bool,
  isEditLocked: PropTypes.bool,
  hasDraft: PropTypes.bool,
  onEnterEditMode: PropTypes.func.isRequired,
  onExitEditMode: PropTypes.func.isRequired,
  onSaveDraft: PropTypes.func.isRequired,
  onPreparePublish: PropTypes.func.isRequired,
  onClearDraft: PropTypes.func.isRequired,
  onCommitDraft: PropTypes.func.isRequired,
  onCheckPendingChanges: PropTypes.func.isRequired,
  onDiscardChanges: PropTypes.func.isRequired,
  hasPendingChangesRef: PropTypes.shape({ current: PropTypes.bool }),
  onRegisterLeaveConfirmation: PropTypes.func,
  projectId: PropTypes.string,
  requestEditDialog: PropTypes.shape({
    visible: PropTypes.bool,
    holderDisplayName: PropTypes.string,
    isWaiting: PropTypes.bool,
    isRequesting: PropTypes.bool,
    onCancel: PropTypes.func,
    onRequest: PropTypes.func,
  }),
  takeoverRequestDialog: PropTypes.shape({
    visible: PropTypes.bool,
    requesterDisplayName: PropTypes.string,
    isResponding: PropTypes.bool,
    onDeny: PropTypes.func,
    onAccept: PropTypes.func,
  }),
};

ProjectWizardEditControls.defaultProps = {
  hasDraft: false,
  isProjectReady: false,
  isEditLocked: false,
  hasPendingChangesRef: null,
  onRegisterLeaveConfirmation: null,
  projectId: null,
  requestEditDialog: null,
  takeoverRequestDialog: null,
};

export default ProjectWizardEditControls;
